import {CARD_TEXT, CardActionType, CardType, USER_ACTION_CARD_ACTION, UserAction} from "../../../types/game";
import styles from "../GameScreen.module.scss";
import {CardSlotProps} from "../../../types/props";
import {noAction} from "../../../utils";
import React, {useEffect, useRef} from "react";
import {Flipped} from "react-flip-toolkit";
import {useSwipeAction} from "../../../useSwipeAction";

const CardSlot = ({
                      playerId,
                      cardSlot,
                      card,
                      size,
                      disabled,
                      onCardSlotClick,
                      onCardSlotRightClick,
                      game
                  }: CardSlotProps) => {
        const player = game.users.find(u => u.userId === playerId)!;
        const isFaceDown = card.type === CardType.CARD_BACK;
        const isSlotEmpty = card.type === CardType.EMPTY;

        const {gameData: {lastPlayerAction, lastTossCartEvent}} = game;

        const suppressNextClick = useRef(false);

        useEffect(() => {
            return () => {
                suppressNextClick.current = false;
            };
        }, []);

        function constructCardClassName() {
            const sizeClass = size === "sm" ? styles.cardSlot__small : styles.cardSlot__medium;

            const cardClass = isSlotEmpty ?
                styles["cardSlot--empty"] :
                isFaceDown ?
                    styles["cardSlot--back"] :
                    styles["cardSlot--front"];

            const disabledClass = disabled ? styles["cardSlot__disabled"] : "";

            let actionClass = "";
            let tossActionClass = "";

            if (lastPlayerAction) {
                const firstCardMatch = playerId === lastPlayerAction.playerId && cardSlot === lastPlayerAction.cardSlot;
                const secondCardMatch = playerId === lastPlayerAction.playerId2 && cardSlot === lastPlayerAction.cardSlot2;

                if (firstCardMatch || secondCardMatch) {
                    const eventCardAction = USER_ACTION_CARD_ACTION[lastPlayerAction.userAction];

                    switch (eventCardAction) {
                        case CardActionType.MOVED:
                            actionClass = firstCardMatch ? styles["cardSlot--moveSource"] : styles["cardSlot--moveTarget"];
                            break;
                        case CardActionType.SELECTED:
                            actionClass = styles["cardSlot--selected"];
                            break;
                        case CardActionType.SWAPPED:
                            if (lastPlayerAction.swapped) {
                                actionClass = styles["cardSlot--swapped"];
                            }
                            break;
                        case CardActionType.NOTHING:
                        default:
                    }
                }
            }

            const lastEventWasToss = (lastTossCartEvent?.version ?? 0) >= (lastPlayerAction?.version ?? 0)

            if (lastTossCartEvent && lastEventWasToss) {
                const tossedCardMatch = playerId === lastTossCartEvent.playerId && cardSlot === lastTossCartEvent.cardSlot;
                const punishCardMatch = playerId === lastTossCartEvent.eventInitiatorId && cardSlot === lastTossCartEvent.punishCardSlot;

                if (tossedCardMatch) {
                    tossActionClass = styles["cardSlot--tossMarker"];
                } else if (punishCardMatch) {
                    tossActionClass = styles["cardSlot--punishMarker"];
                }
            }

            let finalActionClass = `${actionClass} ${tossActionClass}`;

            if (actionClass && tossActionClass) {
                finalActionClass = (lastPlayerAction?.version ?? 0) > (lastTossCartEvent?.version ?? 0) ? actionClass : tossActionClass;
            }

            return `${styles.cardSlot} ${cardClass} ${disabledClass} ${sizeClass} ${finalActionClass}`;
        }

        function onTapAction() {
            onCardSlotClick(playerId, cardSlot);
        }

        function doToss() {
            const userAction = player.gameUserData.actions.tossAction
                ? UserAction.GIVE_CARD_CHOOSE_CARD
                : UserAction.TOSS_CARD;

            onCardSlotRightClick(playerId, cardSlot, userAction);
        }

        function onContextMenu(e: React.MouseEvent) {
            e.preventDefault();
            doToss();
        }

        const swipeHandlers = useSwipeAction(
            () => {
                suppressNextClick.current = true;
                onTapAction();
            },
            () => {
                suppressNextClick.current = true;
                doToss();
            }
        );

        function onClick(e: React.MouseEvent) {
            if (suppressNextClick.current) {
                suppressNextClick.current = false;
                return;
            }
            onTapAction();
        }

        return (
            <Flipped key={card.id} flipId={`card-${card.id}`}>
                <div
                    key={cardSlot}
                    className={constructCardClassName()}
                    onClick={isSlotEmpty ? noAction : onClick}
                    onContextMenu={isSlotEmpty ? noAction : onContextMenu}
                    {...(isSlotEmpty ? {} : swipeHandlers)}
                >
                    {!isFaceDown && (
                        <span className={styles.cardSlot__label}>{CARD_TEXT[card.type]}</span>
                    )}
                </div>
            </Flipped>
        );
    }
;

export default CardSlot;
